import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectSpecifications } from '../redux/specification/specificationSelectors'
import {
  currencySymbol,
  formatDecimal,
  formatMoney,
  formatPercent,
  numberLocale,
} from '../utils/formatNumbers'

/**
 * Locale-aware number formatters: separators follow the UI language,
 * the currency symbol follows the organisation currency.
 */
export function useNumberFormat() {
  const { i18n } = useTranslation()
  const currency = useSelector(selectSpecifications)?.[0]?.Currency as string | undefined
  const locale = numberLocale(i18n.language)

  return useMemo(
    () => ({
      locale,
      currency,
      symbol: currencySymbol(currency, locale),
      money: (value: unknown, decimals = 2) => formatMoney(value, currency, { locale, decimals }),
      decimal: (value: unknown, decimals = 2) => formatDecimal(value, { locale, decimals }),
      integer: (value: unknown) => formatDecimal(value, { locale, decimals: 0 }),
      percent: (value: unknown, decimals = 2) => formatPercent(value, { locale, decimals }),
    }),
    [locale, currency]
  )
}
